import React, { useState } from 'react';
import { Box, TextField, Button, Paper, Typography, Fade, useTheme, Zoom, CircularProgress, InputAdornment } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';

interface EmotionalInputProps {
  onSubmit: (input: string) => void;
  isLoading: boolean;
}

const EmotionalInput: React.FC<EmotionalInputProps> = ({ onSubmit, isLoading }) => {
  const [input, setInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const theme = useTheme();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      onSubmit(input.trim());
      setInput('');
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 5 },
        borderRadius: 6,
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.6)',
        boxShadow: isFocused
          ? '0 20px 40px -10px rgba(15, 76, 58, 0.15)'
          : '0 10px 30px -10px rgba(0,0,0,0.05)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      {/* Decorative Icon */}
      <Box
        sx={{
          position: 'absolute',
          top: -30,
          right: { xs: '50%', md: 40 },
          transform: { xs: 'translateX(50%)', md: 'none' },
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 20px rgba(212, 175, 55, 0.4)',
          color: 'white',
          zIndex: 2,
        }}
      >
        <PsychologyIcon sx={{ fontSize: 32 }} />
      </Box>

      <Box component="form" onSubmit={handleSubmit} dir="rtl">
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            color: 'primary.main',
            mb: 1,
            mt: 2
          }}
        >
          كيف تشعر اليوم؟
        </Typography>

        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
          sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}
        >
          اكتب ما في قلبك بصدق، ودعنا نساعدك في العثور على السكينة من خلال آيات القرآن الكريم
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="أشعر بـ..."
          variant="outlined"
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mt: 1, alignSelf: 'flex-start' }}>
                <AutoAwesomeIcon sx={{ color: 'secondary.main', opacity: 0.7 }} />
              </InputAdornment>
            ),
          }}
          inputProps={{
            maxLength: 200
          }}
          helperText={
            <Typography variant="caption" sx={{ textAlign: 'left', display: 'block', mt: 0.5, color: input.length >= 200 ? 'error.main' : 'text.secondary' }}>
              {input.length} / 200
            </Typography>
          }
          sx={{
            mb: 4,
            '& .MuiOutlinedInput-root': {
              p: 3,
              textAlign: 'right',
              direction: 'rtl',
              fontFamily: '"Amiri", "Noto Naskh Arabic", serif',
              fontSize: '1.25rem',
              lineHeight: 1.8,
              backgroundColor: '#FAFAFA',
              borderRadius: 4,
              border: '2px solid transparent',
              transition: 'all 0.3s ease',
              '& fieldset': { border: 'none' },
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
              '&.Mui-focused': {
                backgroundColor: '#FFFFFF',
                boxShadow: `0 0 0 4px rgba(46, 93, 75, 0.15)`,
                border: `2px solid rgba(46, 93, 75, 0.3)`,
              }
            },
            '& .MuiFormHelperText-root': {
              textAlign: 'left',
              mx: 1
            }
          }}
        />

        <Zoom in={true} style={{ transitionDelay: '100ms' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!input.trim() || isLoading}
            fullWidth
            sx={{
              fontFamily: '"Noto Naskh Arabic", "traditional arabic", serif',
              fontSize: '1.2rem',
              py: 1.5,
              borderRadius: 2,
              background: isLoading
                ? 'rgba(0, 0, 0, 0.12)'
                : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: '#fff',
              boxShadow: isLoading ? 'none' : '0 4px 15px rgba(46, 125, 50, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(46, 125, 50, 0.4)',
              },
              '&.Mui-disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
                boxShadow: 'none',
              }
            }}
            startIcon={isLoading ? <CircularProgress size={24} color="inherit" /> : <SendIcon sx={{ ml: 1 }} />}
          >
            {isLoading ? 'جاري البحث عن الإرشاد...' : 'احصل على الإرشاد القرآني'}
          </Button>
        </Zoom>
      </Box>
    </Paper>
  );
};

export default EmotionalInput; 